import type { DashboardView } from '../components/Common/MobileDashboardDock'
import type { WorkspaceActivityRecord } from '../services/activityService'
import type { ChecklistItem } from '../types/schema'
import type {
  DashboardChecklistVisualItem,
  DashboardVisualItem,
  DueDateRelativity,
  RecurringCadence,
  RecurringOccurrenceProjection,
} from '../types/dashboardVisualItem'
import type { TimelineElement } from '../types/timelineElement'
import { EMPTY_RISK_SCORE, buildRiskByElementId, getChecklistRiskScore } from './riskAggregator'
import { getChecklistBlockState } from './checklistDependencyState'
import { computeTodayContext } from './todayContext'
import { getChecklistScheduleSpan } from '../services/checklistDurationHelpers'
import { buildLockGraph, resolveLock } from '../services/checklistLockService'
import { isoWeekKey } from '../services/recurringChecklistService'
import type { RepeatPagesInput } from '../services/repeat/types'

const FALLBACK_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)', 'var(--chart-6)'] as const

const DAY_MS = 24 * 60 * 60 * 1000
/** Hard cap per recurring source, keeping the cadence trail legible even for
 *  year-long DAILY items inside a wide view window. See Reviewer's Addendum
 *  section C (Anti-clutter mechanics) in the plan. */
const RECURRING_TRAIL_CAP = 12

const toDayIso = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') return null
  const day = value.length >= 10 ? value.slice(0, 10) : value
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null
}

const dayIsoFromMs = (ms: number, timezone: string | undefined): string => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const parts = Object.fromEntries(
    formatter.formatToParts(new Date(ms)).map((part) => [part.type, part.value]),
  )
  return `${parts.year}-${parts.month}-${parts.day}`
}

const relativityFor = (
  raw: string | null | undefined,
  todayIso: string,
): DueDateRelativity => {
  const day = toDayIso(raw)
  if (!day) return null
  if (day < todayIso) return 'past'
  if (day === todayIso) return 'today'
  return 'future'
}

const cadenceFor = (item: ChecklistItem): RecurringCadence => {
  const type = item.checklistType
  if (type === 'DAILY' || type === 'WEEKLY') return type
  return 'INDIVIDUAL'
}

const hasStock = (item: ChecklistItem): boolean =>
  (item.stockRequirements?.length ?? 0) > 0 ||
  (item.requiredStockIds?.length ?? 0) > 0

/** Monday of the ISO week containing `dayIso` (YYYY-MM-DD, local-time math to
 *  match `recurringWindowFilter.ts`'s existing convention). */
const isoWeekStart = (dayIso: string): string => {
  const [y, m, d] = dayIso.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  const dayOfWeek = date.getDay() // 0 = Sun, 1 = Mon
  const distance = (dayOfWeek + 6) % 7
  date.setDate(date.getDate() - distance)
  const yy = date.getFullYear().toString().padStart(4, '0')
  const mm = (date.getMonth() + 1).toString().padStart(2, '0')
  const dd = date.getDate().toString().padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

const addDaysIso = (iso: string, n: number): string => {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y, m - 1, d + n)
  const yy = date.getFullYear().toString().padStart(4, '0')
  const mm = (date.getMonth() + 1).toString().padStart(2, '0')
  const dd = date.getDate().toString().padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

const dayIsoToMsUtc = (iso: string): number => {
  const [y, m, d] = iso.split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}

/** Downsample an ordered array to at most `cap` items, keeping the first ⌈cap/2⌉
 *  and the last ⌊cap/2⌋. Preserves temporal edges — earliest and latest
 *  occurrences are the ones that carry the most narrative weight. */
const boundedTake = <T>(items: readonly T[], cap: number): T[] => {
  if (items.length <= cap) return [...items]
  const head = Math.ceil(cap / 2)
  const tail = cap - head
  return [...items.slice(0, head), ...items.slice(items.length - tail)]
}

const getRootGoalId = (
  element: TimelineElement | null | undefined,
  elementById: ReadonlyMap<string, TimelineElement>,
): string | null => {
  let cursor = element
  let guard = 0
  while (cursor && guard < 20) {
    if (cursor.type === 'GOAL') return cursor.id
    cursor = cursor.parentId ? elementById.get(cursor.parentId) : undefined
    guard += 1
  }
  return null
}

const getAssigneeInitials = (item: ChecklistItem) => {
  const name = item.assigneeName?.trim()
  if (!name) return null
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || null
}

const toCreatedAtMs = (value: unknown): number | null => {
  if (value instanceof Date) return value.getTime()
  if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    const date = value.toDate()
    return date instanceof Date ? date.getTime() : null
  }
  return null
}

const dateSortValue = (item: DashboardVisualItem) => {
  const raw = item.startDate ?? item.endDate
  if (!raw) return Number.POSITIVE_INFINITY
  const parsed = Date.parse(raw)
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY
}

const progressSortValue = (item: DashboardVisualItem) => {
  if (item.kind !== 'CHECKLIST') return item.progress
  return item.isCompleted ? 100 : 0
}

const lifecycleGroupFor = (item: DashboardVisualItem) => {
  if (item.kind === 'CHECKLIST') return item.sourceChecklist?.boardStatus ?? item.workflowStatus
  return item.sourceElement?.boardStatus ?? 'UNSCHEDULED'
}

const buildDependencyEdges = (items: readonly DashboardVisualItem[]) =>
  items.flatMap((item) =>
    (item.dependencyNodeIds ?? []).map((targetId) => ({
      sourceId: item.id,
      targetId,
    })),
  )

const buildUrgencyBuckets = (items: readonly DashboardVisualItem[]) => {
  const buckets = new Map<number, DashboardVisualItem[]>()
  for (const item of items) {
    const urgency = item.kind === 'CHECKLIST' ? item.effectiveUrgency : item.risk.urgency
    const bucket = buckets.get(urgency) ?? []
    bucket.push(item)
    buckets.set(urgency, bucket)
  }
  return buckets
}

const buildLifecycleGroups = (items: readonly DashboardVisualItem[]) => {
  const groups = new Map<string, DashboardVisualItem[]>()
  for (const item of items) {
    const group = lifecycleGroupFor(item)
    const bucket = groups.get(group) ?? []
    bucket.push(item)
    groups.set(group, bucket)
  }
  return groups
}

const recentActivityTargetIds = (
  activityRecords: readonly WorkspaceActivityRecord[],
  now: Date,
) => {
  const cutoff = now.getTime() - 24 * 60 * 60 * 1000
  return new Set(activityRecords
    .filter((activity) => activity.createdAt && activity.createdAt.getTime() >= cutoff)
    .map((activity) => activity.targetId))
}

const applyViewSpecificProjection = (
  allItems: readonly DashboardVisualItem[],
  viewType: DashboardView,
  input: {
    activityRecords?: readonly WorkspaceActivityRecord[]
    now?: Date
  },
) => {
  if (viewType === 'Timeline') {
    return [...allItems].sort((left, right) =>
      dateSortValue(left) - dateSortValue(right) || left.title.localeCompare(right.title),
    )
  }
  if (viewType === 'Radar') {
    return [...allItems].sort((left, right) =>
      right.risk.severity - left.risk.severity ||
      (right.kind === 'CHECKLIST' ? right.effectiveUrgency : right.risk.urgency) -
        (left.kind === 'CHECKLIST' ? left.effectiveUrgency : left.risk.urgency),
    )
  }
  if (viewType === 'Constellation') {
    return allItems.filter((item) => item.kind !== 'CHECKLIST' || item.dependencyNodeIds.length > 0 || item.risk.severity >= 4)
  }
  if (viewType === 'Journey') {
    return [...allItems]
      .filter((item) => item.kind === 'GOAL' || (item.kind === 'CHECKLIST' && !item.isUnscheduled))
      .sort((left, right) =>
        progressSortValue(left) - progressSortValue(right) || dateSortValue(left) - dateSortValue(right),
      )
  }
  if (viewType === 'Stripboard') {
    return [...allItems].sort((left, right) =>
      lifecycleGroupFor(left).localeCompare(lifecycleGroupFor(right)) ||
      dateSortValue(left) - dateSortValue(right) ||
      left.title.localeCompare(right.title),
    )
  }
  if (viewType === 'WarRoom') {
    const recentTargets = recentActivityTargetIds(input.activityRecords ?? [], input.now ?? new Date())
    return allItems.filter((item) =>
      item.risk.severity >= 4 ||
      (item.kind === 'CHECKLIST' && item.isCompletionBlocked) ||
      recentTargets.has(item.id),
    )
  }
  return [...allItems]
}

export const buildViewProjectionSnapshot = (
  timeline: {
    elements: readonly TimelineElement[]
    checklistItems: readonly ChecklistItem[]
    isPublicReadOnly: boolean
  },
  viewType: DashboardView,
  canReadElement: (element: TimelineElement) => boolean,
  input: {
    activityRecords?: readonly WorkspaceActivityRecord[]
    now?: Date
    timezone?: string
    pages?: RepeatPagesInput | null
  } = {},
) => {
  const now = input.now ?? new Date()
  const todayIso = dayIsoFromMs(now.getTime(), input.timezone)
  const elementById = new Map(timeline.elements.map((element) => [element.id, element]))
  const riskByElementId = buildRiskByElementId(timeline.elements, timeline.checklistItems)
  const checklistById = new Map(timeline.checklistItems.map((item) => [item.id, item]))

  const elementItems: DashboardVisualItem[] = timeline.elements
    .filter((element) => (element.board ?? 'DASHBOARD') === 'DASHBOARD')
    .filter(canReadElement)
    .map((element, index) => {
      const risk = riskByElementId.get(element.id) ?? EMPTY_RISK_SCORE
      return {
        kind: element.type,
        source: 'ELEMENT',
        id: element.id,
        workspaceId: element.workspaceId,
        parentId: element.parentId,
        rootGoalId: getRootGoalId(element, elementById),
        title: element.title,
        board: element.board,
        color: element.color ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length],
        startDate: element.startDate ?? null,
        endDate: element.endDate ?? null,
        canMutate: !timeline.isPublicReadOnly,
        isUnscheduled: !element.startDate && !element.endDate,
        risk,
        dependencyNodeIds: element.dependencyNodeIds ?? [],
        sourceElement: element,
        progress: element.progress,
      }
    })

  const checklistItems: DashboardVisualItem[] = timeline.checklistItems
    .filter((item) => (item.board ?? 'DASHBOARD') === 'DASHBOARD')
    .filter((item) => item.nodeKind !== 'GATE_AND' && item.nodeKind !== 'GATE_OR')
    .map((item, index) => {
      const scheduleSpan = getChecklistScheduleSpan(item)
      const parent = elementById.get(item.parentContainerId)
      const risk = getChecklistRiskScore(item)
      const periodKey = item.checklistType === 'WEEKLY' ? isoWeekKey(now, input.timezone) : todayIso
      const blockState = getChecklistBlockState(item, checklistById, periodKey, input.pages ?? undefined, input.timezone)
      return {
        kind: 'CHECKLIST',
        source: 'CHECKLIST_ITEM',
        id: item.id,
        workspaceId: item.workspaceId,
        parentId: item.parentContainerId,
        rootGoalId: getRootGoalId(parent, elementById),
        title: item.title,
        board: item.board ?? 'DASHBOARD',
        color: FALLBACK_COLORS[(index + elementItems.length) % FALLBACK_COLORS.length],
        startDate: scheduleSpan?.startDate ?? null,
        endDate: scheduleSpan?.endDate ?? null,
        canMutate: !timeline.isPublicReadOnly,
        isUnscheduled: scheduleSpan === null,
        risk,
        dependencyNodeIds: item.dependencyNodeIds ?? [],
        sourceChecklist: item,
        workflowStatus: item.workflowStatus ?? 'CREATED',
        previousWorkflowStatus: item.previousWorkflowStatus ?? null,
        isCompleted: item.isCompleted,
        isCompletionBlocked: blockState.isBlocked,
        blockerCount: blockState.blockerCount,
        blockReasons: blockState.reasons,
        blockingNodeIds: blockState.reasons.map((reason) => reason.targetId).filter((id): id is string => typeof id === 'string'),
        urgency: item.urgencyStars ?? 3,
        effectiveUrgency: risk.urgency,
        assigneeInitials: getAssigneeInitials(item),
        createdAtMs: toCreatedAtMs(item.createdAt),
        hasTimeWindow: Boolean(item.hasTimeWindow),
        scheduleTimeFrom: item.scheduleTimeFrom ?? null,
        scheduleTimeTo: item.scheduleTimeTo ?? null,
        searchDateBuckets: item.searchDateBuckets ?? [],
        checklistCadence: cadenceFor(item),
        recurringWindow: item.recurringWindow
          && toDayIso(item.recurringWindow.startDate)
          && toDayIso(item.recurringWindow.endDate)
          ? {
              startDate: toDayIso(item.recurringWindow.startDate) as string,
              endDate: toDayIso(item.recurringWindow.endDate) as string,
            }
          : null,
        hasProofRequirement: Boolean(item.requiresProofFile),
        hasStockRequirement: hasStock(item),
        dueDateRelativity: relativityFor(
          scheduleSpan?.endDate ?? null,
          todayIso,
        ),
      }
    })

  const allItems = [...elementItems, ...checklistItems]
  const visualItems = applyViewSpecificProjection(allItems, viewType, input)

  return {
    visualItems,
    riskByElementId,
    dependencyEdges: buildDependencyEdges(visualItems),
    urgencyBuckets: buildUrgencyBuckets(visualItems),
    lifecycleGroups: buildLifecycleGroups(visualItems),
    todayIso,
    nowMs: now.getTime(),
  }
}

/**
 * Build a bounded per-source occurrence map for recurring checklists inside
 * the current view window. Views ask for one line/trail per source and iterate
 * the returned occurrences — the projection layer owns the "how many" answer
 * so no view accidentally renders a year of dots.
 *
 * Windowing rules:
 *   - Intersect the item's `recurringWindow` with `[viewStartMs, viewEndMs]`.
 *   - DAILY: one occurrence per day.
 *   - WEEKLY: one occurrence per Monday-anchored week.
 *   - Cap at RECURRING_TRAIL_CAP occurrences per source (head + tail).
 *
 * State rules (per occurrence key):
 *   - completed  → `occurrences[key]?.isCompleted === true`
 *   - today      → occurrence date equals today's date (in workspace tz)
 *   - missed     → past + not completed
 *   - blocked    → master item is completion-blocked and occurrence not done
 *   - upcoming   → everything else
 */
export const buildRecurringOccurrenceMap = (
  visualItems: readonly DashboardVisualItem[],
  viewStartMs: number,
  viewEndMs: number,
  input: {
    now?: Date
    timezone?: string
    pages?: RepeatPagesInput | null
  } = {},
): Map<string, RecurringOccurrenceProjection[]> => {
  const result = new Map<string, RecurringOccurrenceProjection[]>()
  if (!Number.isFinite(viewStartMs) || !Number.isFinite(viewEndMs) || viewEndMs <= viewStartMs) {
    return result
  }
  const today = computeTodayContext(viewStartMs, viewEndMs, input)
  const todayIso = dayIsoFromMs(today.todayMs, input.timezone)
  const viewStartIso = dayIsoFromMs(viewStartMs, input.timezone)
  const viewEndIso = dayIsoFromMs(viewEndMs, input.timezone)

  for (const item of visualItems) {
    if (item.kind !== 'CHECKLIST') continue
    const checklist = item as DashboardChecklistVisualItem
    if (checklist.checklistCadence === 'INDIVIDUAL') continue
    if (!checklist.recurringWindow) continue

    const cadence = checklist.checklistCadence
    const winStart = checklist.recurringWindow.startDate
    const winEnd = checklist.recurringWindow.endDate
    // Effective span = intersection of view window and recurring window.
    const spanStart = winStart > viewStartIso ? winStart : viewStartIso
    const spanEnd = winEnd < viewEndIso ? winEnd : viewEndIso
    if (spanStart > spanEnd) continue

    const raw: Array<{ key: string; iso: string }> = []
    if (cadence === 'DAILY') {
      // Walk day-by-day inside the span.
      let cursor = spanStart
      // Hard safety cap to prevent runaway loops on absurd windows (~10 years).
      for (let guard = 0; guard < 3660; guard += 1) {
        if (cursor > spanEnd) break
        raw.push({ key: cursor, iso: cursor })
        cursor = addDaysIso(cursor, 1)
      }
    } else {
      // WEEKLY: walk Monday-to-Monday. The occurrence key is the Monday iso.
      let cursor = isoWeekStart(spanStart)
      for (let guard = 0; guard < 520; guard += 1) {
        // Weekly items overlap a range: include the week whenever its Monday
        // falls on or before spanEnd (the last week can straddle the edge).
        if (cursor > spanEnd) break
        raw.push({ key: cursor, iso: cursor })
        cursor = addDaysIso(cursor, 7)
      }
    }

    const bounded = boundedTake(raw, RECURRING_TRAIL_CAP)
    if (bounded.length === 0) continue

    const source = checklist.sourceChecklist
    const occurrences = source?.occurrences ?? {}
    const isMasterBlocked = checklist.isCompletionBlocked
    const sourceItems = visualItems
      .filter((v): v is DashboardChecklistVisualItem => v.kind === 'CHECKLIST')
      .map((v) => v.sourceChecklist)
      .filter((s): s is ChecklistItem => Boolean(s))
    const graph = source ? buildLockGraph(sourceItems) : null

    const projections: RecurringOccurrenceProjection[] = bounded.map(({ key, iso }) => {
      const rawState = occurrences[key] ?? null
      const isCompleted = rawState?.isCompleted === true
      const lock = (source && graph) ? resolveLock(source, key, graph, input.pages, input.now, input.timezone) : null
      const isKeyBlocked = lock ? (lock.isLocked ?? lock.isBlocked) : isMasterBlocked
      let state: RecurringOccurrenceProjection['state']
      if (isCompleted) state = 'completed'
      else if (iso === todayIso) state = 'today'
      else if (iso < todayIso) state = 'missed'
      else if (isKeyBlocked) state = 'blocked'
      else state = 'upcoming'
      const startMs = dayIsoToMsUtc(iso)
      const endMs = cadence === 'WEEKLY' ? startMs + 6 * DAY_MS : startMs
      return {
        sourceChecklistId: checklist.id,
        occurrenceKey: key,
        occurrenceStartMs: startMs,
        occurrenceEndMs: endMs,
        cadence,
        state,
        rawState,
      }
    })
    result.set(checklist.id, projections)
  }
  return result
}
